// O painel lateral de UMA pessoa do app. Painel lateral e não modal (regra do
// dono): o fundo escurece mas NÃO fecha — fecha no ✕ ou no Esc, e o motivo que
// estava sendo digitado não se perde num clique fora.
import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  FiX, FiKey, FiLock, FiUnlock, FiSlash, FiRotateCcw, FiExternalLink, FiCopy, FiSend, FiDownload, FiSmartphone,
  FiMonitor, FiCheck, FiLoader, FiAlertTriangle,
} from 'react-icons/fi'
import { API_APP, useApp, useEnviar, Carregando, Falha, Selo, Avatar, PAPEL, num, ha, quando, titulo } from './comum'
import { formatPhone, soDigitos } from '../../lib/mascaras'
import type { Pessoa } from './Pessoas'

type Detalhe = Pessoa & {
  foto_em: string | null
  vitrine_detalhe: {
    resumo: Record<string, number>; cidades: { cidade: string; uf: string | null; n: number }[]
    produtos: { produto_id: number; nome?: string; vistas: number; chamados: number }[]
    serie: { dia: string; aberturas: number; pedidos: number }[]
  } | null
  envios: { em: string; motivo: string; rotulo: string; por: string | null; final: string | null; destino: string }[]
  recusas: { em: string; porta: string; rotulo: string }[]
  entradas: { em: string; dispositivo: string | null; pwa: boolean }[]
  pedidos_acesso: { status: string; destino_origem: string | null; motivo: string | null; decidido_por: string | null; decidido_em: string | null; criado_em: string }[]
  acoes: { em: string; acao: string; rotulo: string; por: string | null; motivo: string | null }[]
}

// 'trocar-whatsapp' não é rota própria: é a senha nova COM o número que o escritório digitou (15/09/2026).
type Acao = 'senha-nova' | 'trocar-whatsapp' | 'exigir-troca' | 'cancelar-troca' | 'desbloquear' | 'desativar' | 'reativar'

export function PessoaDrawer({ codigo, gere, aoFechar, aoMudar }: {
  codigo: string; gere: boolean; aoFechar: () => void; aoMudar: () => void
}) {
  const { dados: p, erro, recarregar } = useApp<Detalhe>(`${API_APP}/pessoas/${codigo}`)
  const enviar = useEnviar()
  const [confirmar, setConfirmar] = useState<Acao | null>(null)
  const [motivo, setMotivo] = useState('')
  const [novoZap, setNovoZap] = useState('')
  const [indo, setIndo] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; t: string } | null>(null)
  const [copiou, setCopiou] = useState(false)

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') aoFechar() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [aoFechar])

  const agir = async (acao: Acao) => {
    setIndo(true); setMsg(null)
    const r = acao === 'trocar-whatsapp'
      ? await enviar(`${API_APP}/pessoas/${codigo}/senha-nova`, { celular: soDigitos(novoZap) })
      : await enviar(`${API_APP}/pessoas/${codigo}/${acao}`, acao === 'desativar' ? { motivo } : {})
    setIndo(false)
    if (!r.ok) { setMsg({ ok: false, t: r.erro || 'falhou' }); return }
    const para = r.dados?.origem === 'escritorio' ? 'o WhatsApp do app' : 'o WhatsApp do cadastro'
    const TEXTO: Record<Acao, string> = {
      'senha-nova': `Senha nova enviada para ${para}, final ••${r.dados?.destino || ''}. A antiga deixou de valer.`,
      'trocar-whatsapp': r.dados?.origem === 'escritorio'
        ? `Senha enviada para o número novo, final ••${r.dados?.destino || ''}. Daqui em diante toda senha dela vai para ele, "esqueci" inclusive.`
        : `Senha enviada para o número do cadastro, final ••${r.dados?.destino || ''}. O app voltou a usar o número do cadastro.`,
      'exigir-troca': 'Pronto: na próxima vez que ela abrir o app, vai ter de criar a própria senha.',
      'cancelar-troca': 'Pedido de troca cancelado.',
      desbloquear: 'Desbloqueada — ela já pode tentar entrar de novo.',
      desativar: 'Acesso desativado. Ela sai do app na próxima tela que abrir.',
      reativar: 'Acesso reativado.',
    }
    setMsg({ ok: true, t: r.dados?.aviso ? `${TEXTO[acao]} ⚠️ ${r.dados.aviso}` : TEXTO[acao] })
    setConfirmar(null); setMotivo(''); setNovoZap('')
    recarregar(); aoMudar()
  }

  const destino = (document.querySelector('.neu-stage') as HTMLElement | null) || document.body
  const temTema = !!document.querySelector('.neu-lab')

  return createPortal(
    <div className={temTema ? 'neu-lab' : undefined} style={{ background: 'transparent' }}>
      <div className="fixed inset-0 z-[200] bg-black/40" aria-hidden="true" />
      <aside role="dialog" aria-modal="true" aria-label={p?.nome ? `Pessoa: ${titulo(p.nome)}` : 'Pessoa'}
        className="fixed inset-y-0 right-0 z-[201] w-full sm:w-[460px] bg-card shadow-2xl flex flex-col">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border/60 shrink-0">
          {p ? <Avatar foto={p.foto} nome={p.nome} tamanho={44} /> : <span className="w-11 h-11" />}
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold truncate">{p ? titulo(p.nome) || '(sem nome no Draco)' : 'Abrindo…'}</p>
            {p && <p className="text-[11px] text-muted-foreground truncate"><span className="tabular-nums">{p.codigo}</span>{p.papel && <> · {PAPEL[p.papel]}</>}{p.grupo && <> · {titulo(p.grupo)}</>}{p.distrito && <> · {titulo(p.distrito)}</>}</p>}
          </div>
          <button onClick={aoFechar} aria-label="Fechar" className="neu-relief rounded-full w-8 h-8 inline-flex items-center justify-center shrink-0"><FiX /></button>
        </div>

        {!p ? (erro ? <Falha erro={erro} tentar={recarregar} /> : <Carregando />) : (
          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hidden px-4 py-3 flex flex-col gap-4">
            <div className="flex items-center gap-1.5 flex-wrap">
              {p.ativo ? (p.usando ? <Selo tom="bom">usando</Selo> : <Selo tom="alerta">parada há 7 dias</Selo>) : <Selo><FiSlash className="w-3 h-3" />desativada</Selo>}
              {p.bloqueada && <Selo tom="ruim"><FiLock className="w-3 h-3" />bloqueada até {quando(p.bloqueado_ate)}</Selo>}
              {p.trocar_senha && <Selo tom="info"><FiKey className="w-3 h-3" />{(p as Pessoa & { senha_provisoria?: boolean }).senha_provisoria ? 'com a senha provisória' : 'troca de senha pendente'}</Selo>}
              {p.instalado_em ? <Selo><FiDownload className="w-3 h-3" />instalou</Selo> : <Selo>usa no navegador</Selo>}
              {p.foto ? <Selo>com foto</Selo> : <Selo>sem foto</Selo>}
              {p.no_avanco && <Selo tom="alerta">cadastro no depósito</Selo>}
            </div>

            {msg && (
              <p className={`text-[12px] rounded-xl px-3 py-2 ${msg.ok ? 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300' : 'bg-red-500/10 text-red-700 dark:text-red-300'}`} role="status">{msg.t}</p>
            )}

            <Bloco titulo="Acesso">
              <Par r="Último uso" v={p.ultimo_uso ? `${ha(p.ultimo_uso)} (${quando(p.ultimo_uso)})` : 'nunca abriu'} />
              <Par r="Última entrada com senha" v={p.ultimo_login ? quando(p.ultimo_login) : '—'} />
              <Par r="Acesso desde" v={quando(p.criado_em)} />
              <Par r="Instalou o app" v={p.instalado_em ? quando(p.instalado_em) : 'não'} />
              <Par r="WhatsApp do cadastro" v={p.whatsapp_app ? <span className="text-muted-foreground line-through">{p.whatsapp || 'sem WhatsApp'}</span> : p.whatsapp || 'sem WhatsApp'} />
              {p.whatsapp_app && <Par r="WhatsApp do app" v={`${p.whatsapp_app.numero}${p.whatsapp_app.por ? ` · trocado por ${p.whatsapp_app.por}` : ''}${p.whatsapp_app.em ? ` em ${quando(p.whatsapp_app.em)}` : ''}`} />}
              <Par r="Senha" v={p.senha.origem === 'escolhida' ? `criada por ela em ${quando(p.senha.trocada_em)}` : p.senha.origem === 'enviada' ? 'a que chegou pelo WhatsApp' : 'sem senha'} />
              {p.senha.ultimo_envio && <Par r="Última senha enviada" v={`${quando(p.senha.ultimo_envio.em)} · ${p.senha.ultimo_envio.rotulo}${p.senha.ultimo_envio.por ? ` (${p.senha.ultimo_envio.por})` : ''}`} />}
              {p.trocar_senha && <Par r="Troca pedida" v={`${quando(p.trocar_pedido_em)}${p.trocar_pedido_por ? ` por ${p.trocar_pedido_por}` : ''}`} />}
              {!p.ativo && <Par r="Desativada" v={`${quando(p.desativado_em)}${p.desativado_por ? ` por ${p.desativado_por}` : ''}`} />}
              {p.falhas_login > 0 && <Par r="Senhas erradas seguidas" v={num(p.falhas_login)} />}
            </Bloco>

            {gere && (
              <Bloco titulo="Ações do escritório">
                {confirmar ? (
                  <div className="neu-inset rounded-xl p-3 flex flex-col gap-2">
                    <p className="text-[12.5px] font-semibold flex items-center gap-1.5"><FiAlertTriangle className="w-4 h-4 text-amber-600" />{
                      { 'senha-nova': 'Mandar uma senha nova agora?', 'trocar-whatsapp': 'Trocar o WhatsApp e mandar a senha', desativar: 'Desativar o acesso dela?', 'exigir-troca': 'Exigir que ela crie a própria senha?', 'cancelar-troca': 'Cancelar o pedido de troca?', desbloquear: 'Desbloquear?', reativar: 'Reativar o acesso?' }[confirmar]
                    }</p>
                    <p className="text-[11.5px] text-muted-foreground">{
                      {
                        'senha-nova': p.whatsapp_app
                          ? `Vai para o WhatsApp do app (${p.whatsapp_app.numero}), que o escritório pôs no lugar do cadastro. A senha de agora deixa de valer na hora — combine com ela antes.`
                          : `Vai para o WhatsApp do cadastro (${p.whatsapp || 'sem número'}). A senha de agora deixa de valer na hora — combine com ela antes.`,
                        'trocar-whatsapp': `Para quando o número do cadastro (${p.whatsapp || 'sem número'}) é antigo. A senha vai para o número abaixo e ele passa a ser o WhatsApp dela no app — o "esqueci" também vai para lá. O cadastro do Draco não muda. Só use se confirmou com ela que o número é dela.`,
                        desativar: 'Ela sai do app e não entra mais, nem com a senha certa, até alguém reativar. A vitrine dela continua no ar.',
                        'exigir-troca': 'Na próxima vez que abrir o app, ela vai ter de digitar a senha de agora e criar uma nova antes de seguir.',
                        'cancelar-troca': 'O app volta a abrir normal para ela.',
                        desbloquear: 'Zera as senhas erradas e libera uma nova tentativa agora.',
                        reativar: 'Ela volta a entrar com a senha que já tem.',
                      }[confirmar]
                    }</p>
                    {confirmar === 'trocar-whatsapp' && (
                      <label className="flex flex-col gap-1 text-[11.5px]">
                        <span className="font-medium">WhatsApp novo, com DDD</span>
                        <input value={novoZap} onChange={e => setNovoZap(formatPhone(e.target.value))} inputMode="tel" autoFocus
                          placeholder="(67) 99999-9999"
                          className="neu-inset rounded-lg px-2.5 py-1.5 text-[13px] tabular-nums bg-transparent outline-none" />
                      </label>
                    )}
                    {confirmar === 'desativar' && (
                      <label className="flex flex-col gap-1 text-[11.5px]">
                        <span className="font-medium">Motivo (obrigatório, fica registrado)</span>
                        <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={2} maxLength={200} autoFocus
                          placeholder="Ex.: saiu da empresa; celular roubado"
                          className="neu-inset rounded-lg px-2.5 py-1.5 text-[12.5px] bg-transparent outline-none resize-none" />
                      </label>
                    )}
                    <div className="flex items-center gap-2">
                      <button onClick={() => agir(confirmar)} disabled={indo || (confirmar === 'desativar' && motivo.trim().length < 5) || (confirmar === 'trocar-whatsapp' && soDigitos(novoZap).length < 10)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-semibold disabled:opacity-40 ${confirmar === 'desativar' ? 'bg-red-600 text-white' : 'bg-primary text-primary-foreground'}`}>
                        {indo ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiCheck className="w-3.5 h-3.5" />}Confirmar
                      </button>
                      <button onClick={() => { setConfirmar(null); setMotivo(''); setNovoZap('') }} disabled={indo} className="text-[12px] text-muted-foreground underline">cancelar</button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {p.ativo && <BotaoAcao icon={FiSend} onClick={() => setConfirmar('senha-nova')} disabled={!p.whatsapp && !p.whatsapp_app} title={!p.whatsapp && !p.whatsapp_app ? 'Sem WhatsApp — use "Trocar WhatsApp e mandar senha"' : undefined}>Mandar senha nova</BotaoAcao>}
                    {p.ativo && <BotaoAcao icon={FiSmartphone} onClick={() => setConfirmar('trocar-whatsapp')}>Trocar WhatsApp e mandar senha</BotaoAcao>}
                    {p.ativo && (p.trocar_senha
                      ? <BotaoAcao icon={FiRotateCcw} onClick={() => setConfirmar('cancelar-troca')}>Cancelar troca de senha</BotaoAcao>
                      : <BotaoAcao icon={FiKey} onClick={() => setConfirmar('exigir-troca')}>Exigir troca de senha</BotaoAcao>)}
                    {p.bloqueada && <BotaoAcao icon={FiUnlock} onClick={() => setConfirmar('desbloquear')}>Desbloquear</BotaoAcao>}
                    {p.ativo
                      ? <BotaoAcao icon={FiSlash} perigo onClick={() => setConfirmar('desativar')}>Desativar acesso</BotaoAcao>
                      : <BotaoAcao icon={FiRotateCcw} onClick={() => setConfirmar('reativar')}>Reativar acesso</BotaoAcao>}
                  </div>
                )}
              </Bloco>
            )}

            <Bloco titulo="Vitrine (30 dias)">
              {p.vitrine ? (
                <>
                  <div className="flex items-center gap-2 neu-inset rounded-xl px-3 py-2">
                    <span className="text-[12px] truncate flex-1 tabular-nums" title={p.vitrine.url}>{p.vitrine.url.replace(/^https?:\/\//, '')}</span>
                    <button onClick={() => { navigator.clipboard?.writeText(p.vitrine!.url); setCopiou(true); setTimeout(() => setCopiou(false), 1500) }} aria-label="Copiar link" className="text-muted-foreground hover:text-foreground">
                      {copiou ? <FiCheck className="w-3.5 h-3.5 text-emerald-600" /> : <FiCopy className="w-3.5 h-3.5" />}
                    </button>
                    <a href={p.vitrine.url} target="_blank" rel="noopener noreferrer" aria-label="Abrir a vitrine" className="text-muted-foreground hover:text-foreground"><FiExternalLink className="w-3.5 h-3.5" /></a>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {([['Visitas', p.vitrine.aberturas], ['Folhearam', p.vitrine.folhearam], ['Chamados', p.vitrine.chamados], ['Pedidos', p.vitrine.pedidos], ['Artes', p.vitrine.artes], ['Visitas desde sempre', p.vitrine.aberturas_sempre]] as const).map(([r, v]) => (
                      <div key={r} className="neu-inset rounded-lg px-2 py-1.5 min-w-0">
                        <span className="block text-[10px] text-muted-foreground truncate">{r}</span>
                        <span className="block text-[15px] font-semibold tabular-nums">{num(v)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1.5">Última visita: {p.vitrine.ultima_visita ? `${ha(p.vitrine.ultima_visita)} (${quando(p.vitrine.ultima_visita)})` : 'nunca'}</p>
                  {p.vitrine_detalhe?.produtos?.length ? (
                    <div className="mt-2">
                      <p className="text-[11px] font-semibold text-muted-foreground mb-1">Produtos mais vistos</p>
                      {p.vitrine_detalhe.produtos.slice(0, 5).map(x => (
                        <div key={x.produto_id} className="flex justify-between gap-2 text-[11.5px]"><span className="truncate">{x.nome || `produto ${x.produto_id}`}</span><span className="tabular-nums text-muted-foreground shrink-0">{num(x.vistas)}</span></div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : <p className="text-[12px] text-muted-foreground">Sem link de vitrine (a distribuição não tem vitrine).</p>}
            </Bloco>

            <Bloco titulo={`Entradas com senha (${p.entradas.length})`}>
              {p.entradas.length ? p.entradas.map((e, i) => (
                <Evento key={i} em={e.em} icone={e.dispositivo === 'computador' ? FiMonitor : FiSmartphone}>
                  {e.dispositivo || 'aparelho'}{e.pwa ? ' · app instalado' : ''}
                </Evento>
              )) : <p className="text-[12px] text-muted-foreground">Nenhuma registrada — o histórico de entradas começou em 14/09/2026.</p>}
            </Bloco>

            <Bloco titulo={`Senhas enviadas (${p.envios.length})`}>
              {p.envios.length ? p.envios.map((e, i) => (
                <Evento key={i} em={e.em} icone={FiSend}>
                  {e.rotulo}{e.final ? ` · final ••${e.final}` : ''}{e.destino === 'informado' ? ' · número INFORMADO' : e.destino === 'escritorio' ? ' · WhatsApp trocado pelo escritório' : ''}{e.por ? ` · por ${e.por}` : ''}
                </Evento>
              )) : <p className="text-[12px] text-muted-foreground">Nenhuma.</p>}
            </Bloco>

            {(p.acoes.length > 0 || p.recusas.length > 0 || p.pedidos_acesso.length > 0) && (
              <Bloco titulo="Histórico">
                {[
                  ...p.acoes.map(a => ({ em: a.em, txt: `${a.rotulo}${a.por ? ` · ${a.por}` : ''}${a.motivo ? ` — “${a.motivo}”` : ''}`, icone: FiKey })),
                  ...p.recusas.map(r => ({ em: r.em, txt: `${r.porta === 'entrar' ? 'Não entrou' : 'Pedido de senha recusado'}: ${r.rotulo}`, icone: FiLock })),
                  ...p.pedidos_acesso.map(s => ({ em: s.criado_em, txt: `Pediu acesso · ${s.status}${s.decidido_por ? ` por ${s.decidido_por}` : ''}${s.motivo ? ` — ${s.motivo}` : ''}`, icone: FiCheck })),
                ].sort((a, b) => new Date(b.em).getTime() - new Date(a.em).getTime()).slice(0, 40).map((x, i) => (
                  <Evento key={i} em={x.em} icone={x.icone}>{x.txt}</Evento>
                ))}
              </Bloco>
            )}
          </div>
        )}
      </aside>
    </div>,
    destino,
  )
}

function Bloco({ titulo: t, children }: { titulo: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{t}</h3>
      <div className="flex flex-col gap-1">{children}</div>
    </section>
  )
}

function Par({ r, v }: { r: string; v: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 text-[12px] py-0.5">
      <span className="text-muted-foreground shrink-0">{r}</span>
      <span className="text-right min-w-0 break-words">{v}</span>
    </div>
  )
}

function Evento({ em, icone: Icone, children }: { em: string; icone: React.ComponentType<{ className?: string }>; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-[11.5px] py-0.5">
      <Icone className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
      <span className="flex-1 min-w-0 break-words">{children}</span>
      <span className="text-muted-foreground tabular-nums shrink-0">{quando(em)}</span>
    </div>
  )
}

function BotaoAcao({ icon: Icon, children, onClick, perigo, disabled, title }: {
  icon: React.ComponentType<{ className?: string }>; children: ReactNode; onClick: () => void; perigo?: boolean; disabled?: boolean; title?: string
}) {
  return (
    <button onClick={onClick} disabled={disabled} title={title}
      className={`neu-relief inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[12.5px] font-medium text-left disabled:opacity-40 ${perigo ? 'text-red-700 dark:text-red-300' : ''}`}>
      <Icon className="w-4 h-4 shrink-0" />{children}
    </button>
  )
}
